import { Instant, LocalDate, Year } from "./JSJodaJSON";
import {
  AgencyId,
  AgencyMemberId,
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleId,
  TrainingCenterBundleItemFullDescriptionId,
  TrainingCenterBundleItemId,
  TrainingCenterBundleItemTestQuestionId,
  TrainingCenterBundleItemVideoEngmtQuestionId,
  TrainingCenterBundleItemVideoMediaOverlayId,
} from "./ObjectIds";
import { OfficeInfo } from "./Office";

export interface TrainingCenterCaregiverBundle {
  agencyId: AgencyId;
  bundleId: TrainingCenterBundleId;
  bundleDueDateId: TrainingCenterBundleDueDateId;
  bundleDueDateYear: number;
  visibleDate: LocalDate;
  completionDate: LocalDate;
  complianceDate: LocalDate;

  bundleParams: TrainingCenterBundleParams;
  bundleDisplayData: TrainingCenterTranslated<TrainingCenterDisplayData>[];

  completionCertificate: TrainingCenterBundleCertificate | null;

  bundleItems: TrainingCenterBundleItem[];
  showDuration: boolean;
}

export interface TrainingCenterBundleCertificate {
  createdAt: Instant;
}

export interface TrainingCenterBundleItem {
  bundleItemId: TrainingCenterBundleItemId;

  timeForCertificateMinutes: number | null;

  media: TrainingCenterBundleItemMedia;

  itemCompleted: boolean;
  itemExempt: boolean;

  caregiverSignatureDataURL: string | null;

  /**
   * The test becomes unlocked when the caregiver has watched enough of the
   * training material (e.g. Video, PDF...)
   */
  testUnlocked: boolean;

  /**
   * How many attempts has this caregiver submitted the test. If this is
   * equal to or greater than `testMaxAttemptsAllowed` then the test
   * questions should be grayed out.
   */
  testNumAttempts: number;

  testParams: TrainingCenterTestParams;

  displayData: TrainingCenterTranslated<TrainingCenterBundleItemDisplayData>[];
  currentTestQuestions: TrainingCenterCaregiverTestQuestion[];
  fullDescriptions: TrainingCenterBundleItemFullDescription[];
}

export interface TrainingCenterBundleItemDisplayData {
  title: string | null;
  label: string | null;
  description: string | null;
  sectionHeader: string | null;
  sectionDescription: string | null;

  testSuccessMessage: string | null;
  testFailMessage: string | null;
  testRetryMessage: string | null;

  thumbnailUrl: string | null;
}

export interface TrainingCenterTestParams {
  versionNum: number;

  /**
   * How many wrong answers are allowed to still pass the test
   */
  maxWrongAnswers: number;

  /**
   * If the caregiver failed the test, should we show her which answers she
   * got wrong?
   */
  showWrongAnswers: boolean;

  /**
   * How many total attempts are allowed for this test?
   */
  testMaxAttemptsAllowed: number;
}

/**
 * See `TrainingCenterTestParams` for documentation about the fields
 */
export interface TrainingCenterEditTestParams {
  maxWrongAnswers?: number;
  showWrongAnswers?: boolean;
  testMaxAttemptsAllowed?: number;
}

export interface TrainingCenterCaregiverTestQuestion {
  questionId: TrainingCenterBundleItemTestQuestionId;
  displayData: TrainingCenterTranslated<TrainingCenterTestQuestionDisplayData>[];

  /**
   * The currently selected answer, or the submitted answer from the last
   * submitted test
   */
  markedAnswer: number | null;

  /**
   * The marked answer is known to be the correct answer (its value is 0 and
   * was submitted as a test)
   */
  markedAnswerCorrect: boolean;

  /**
   * Submitted answers that were incorrect (from all previous test
   * submissions)
   */
  incorrectlyGuessedAnswers: number[];
}

export interface TrainingCenterTestQuestionDisplayData {
  questionText: string;

  /**
   * The first answer is always the correct answer
   */
  questionAnswers: string[];
}

export interface TrainingCenterBundleItemFullDescription {
  itemId: TrainingCenterBundleItemFullDescriptionId;
  fullDescriptionData: TrainingCenterTranslated<TrainingCenterBundleItemFullDescriptionData>[];
}

export interface TrainingCenterBundleItemFullDescriptionData {
  title?: string;
  subtitle?: string;
  text?: string;
  imageUrl?: string;
  imageDescription?: string;
  imageFileKey?: string;
  speechFiles: {
    titleSpeechAudioFileUrl?: string;
    titleSpeechMarksFileUrl?: string;
    subtitleSpeechAudioFileUrl?: string;
    subtitleSpeechMarksFileUrl?: string;
    textSpeechAudioFileUrl?: string;
    textSpeechMarksFileUrl?: string;
  };
}

export type TrainingCenterBundleItemMedia =
  | TrainingCenterBundleItemMedia.Video
  | TrainingCenterBundleItemMedia.PDF
  | TrainingCenterBundleItemMedia.TestOnly;

export type TrainingCenterBundleVideoSource = "YOUTUBE" | "VIDEO_URL";

export interface TrainingCenterVideoSubtitles {
  subtitlesUrl: string;
}

export interface TrainingCenterBundleItemVideoDimensions {
  width: number | null;
  height: number | null;
}

export interface TrainingCenterBundleItemVideoEngagementQuestionAnswer {
  value: string;
  isCorrect: boolean;
}

export interface TrainingCenterBundleItemVideoEngagementQuestion {
  questionId: TrainingCenterBundleItemVideoEngmtQuestionId;
  atSecond: number;
  duration: number;

  displayData: TrainingCenterTranslated<TrainingCenterBundleItemVideoEngagementQuestionDisplayData>[];
}

export interface TrainingCenterBundleItemVideoEngagementQuestionDisplayData {
  questionText: string;
  questionAnswers: string[];
}

export interface TrainingCenterBundleItemVideoMediaOverlay {
  itemId: TrainingCenterBundleItemVideoMediaOverlayId;
  atSecond: number;
  duration: number;
  position: TrainingCenterBundleItemVideoMediaOverlayPosition;
  overlayData: TrainingCenterTranslated<TrainingCenterBundleItemVideoMediaOverlayData>[];
}

export interface TrainingCenterBundleItemVideoMediaOverlayData {
  title?: string;
  description?: string;
  imageUrl?: string;
  imageFileKey?: string;
  imageWidth?: number;
}

export declare namespace TrainingCenterBundleItemMedia {
  export interface Video {
    type: "Video";
    requiredWatchTimePercent: number;

    source: TrainingCenterBundleVideoSource;
    engagementQuestions: TrainingCenterBundleItemVideoEngagementQuestion[];
    mediaOverlays: TrainingCenterBundleItemVideoMediaOverlay[];
    videoInfo: TrainingCenterTranslated<TrainingCenterVideoInfo>[];
    videoSubtitles: TrainingCenterTranslated<TrainingCenterVideoSubtitles>[];
    videoWatchData: TrainingCenterTranslated<TrainingCenterCaregiverVideoWatchData>[];
  }

  export interface PDF {
    type: "PDF";
    requiredViewTimeMinutes: number;
    pdfFileDownloadable: boolean;

    pdfInfo: TrainingCenterTranslated<TrainingCenterPDFInfo>[];
    pdfWatchData: TrainingCenterCaregiverPDFWatchData;
  }

  export interface TestOnly {
    type: "TestOnly";
  }
}

export interface TrainingCenterCaregiverVideoWatchData {
  totalSecondsWatched: number;

  /**
   * Array of segments that were watched: `[startSecond, endSecond]`
   */
  watchedSegments: [number, number][];
}

export const trainingCenterCaregiverVideoWatchDataEmpty: TrainingCenterCaregiverVideoWatchData = {
  totalSecondsWatched: 0,
  watchedSegments: [],
};

export function trainingCenterVideoWatchedPercent(
  videoInfo: TrainingCenterVideoInfo,
  watchData: TrainingCenterCaregiverVideoWatchData
): number {
  return Math.min(
    100,
    Math.floor((100 * watchData.totalSecondsWatched) / videoInfo.videoLengthSeconds)
  );
}

export interface TrainingCenterVideoInfo {
  videoUrl: string;
  thumbnailUrl: string;
  videoLengthSeconds: number;
  videoDimensions: TrainingCenterBundleItemVideoDimensions;
}

export interface TrainingCenterPDFInfo {
  pdfUrl: string;
}

export interface TrainingCenterCaregiverPDFWatchData {
  totalSecondsWatched: number;
}

export interface TrainingCenterBundleParams {
  chatAgencyMemberId: AgencyMemberId;
  signingAgencyMemberId: AgencyMemberId;
  signingAgencyMemberTitle: string;
  enforceWatchOrder: boolean;
  requiredForBillingCompliance: boolean;
  canWatchDuringVisitShift: boolean;
  showBundleAfterDueDate: boolean;
  caregiverCertifications: string[];
  isOrientation: boolean;
}

export interface TrainingCenterDisplayData {
  title: string;
  thumbnailUrl: string | null;
}

export interface CaregiverTrainingCenterBundles {
  bundles: TrainingCenterCaregiverBundle[];
}

export interface TrainingCenterTranslated<T> {
  language: TrainingCenterDisplayLanguage;
  data: T;
}

/** @TJS-type string */
export class TrainingCenterDisplayLanguage {
  private constructor() {}

  "TrainingCenterDisplayLanguage": TrainingCenterDisplayLanguage;

  public static wrap(id: string): TrainingCenterDisplayLanguage {
    return <any>id;
  }

  public static unwrap(val: TrainingCenterDisplayLanguage): string {
    return <any>val;
  }
}

export interface TrainingCenterCaregiverVideoBundleItemWatchSegmentParams {
  startSeconds: number;
  endSeconds: number;
}

export interface TrainingCenterCaregiverPDFBundleItemWatchSegmentParams {
  language: TrainingCenterDisplayLanguage;
  secondsWatched: number;
}

export type TrainingCenterCaregiverSubmitTestResult =
  | TrainingCenterCaregiverSubmitTestResult.NotFound
  | TrainingCenterCaregiverSubmitTestResult.BundleNotVisibleToCaregiver
  | TrainingCenterCaregiverSubmitTestResult.OutdatedTestVersion
  | TrainingCenterCaregiverSubmitTestResult.AlreadyCompletedBundleItem
  | TrainingCenterCaregiverSubmitTestResult.ExemptFromBundleItem
  | TrainingCenterCaregiverSubmitTestResult.TooManyFailedAttempts
  | TrainingCenterCaregiverSubmitTestResult.TestNotUnlocked
  | TrainingCenterCaregiverSubmitTestResult.QuestionAnswersInvalid
  | TrainingCenterCaregiverSubmitTestResult.Success;

export declare namespace TrainingCenterCaregiverSubmitTestResult {
  export interface NotFound {
    type: "NotFound";
  }

  export interface BundleNotVisibleToCaregiver {
    type: "BundleNotVisibleToCaregiver";
  }

  export interface OutdatedTestVersion {
    type: "OutdatedTestVersion";
    submittedTestVersionNum: number;
    currentTestVersionNum: number;
  }

  export interface AlreadyCompletedBundleItem {
    type: "AlreadyCompletedBundleItem";
  }

  export interface ExemptFromBundleItem {
    type: "ExemptFromBundleItem";
  }

  export interface TooManyFailedAttempts {
    type: "TooManyFailedAttempts";
  }

  export interface TestNotUnlocked {
    type: "TestNotUnlocked";
  }

  export interface QuestionAnswersInvalid {
    type: "QuestionAnswersInvalid";
  }

  export interface Success {
    type: "Success";
    passedTest: boolean;
    bundle: TrainingCenterCaregiverBundle;
  }
}

export interface TrainingCenterCaregiverQuestionAnswer {
  questionId: TrainingCenterBundleItemTestQuestionId;
  answer: number;
}

export interface TrainingCenterCaregiverSubmitTestParams {
  testVersionNum: number;

  /**
   * The language that was displayed to the caregiver when she submitted the
   * test.
   */
  language: TrainingCenterDisplayLanguage;

  caregiverSignatureDataURL: string;
  questionAnswers: TrainingCenterCaregiverQuestionAnswer[];
}

export interface TrainingCenterCaregiverCertificatePdf {
  url: string;
}

export interface BundleOfficeInfo {
  offices: OfficeInfo[];
}

export interface TrainingCenterCaregiverBundlesSummary {
  bundles: TrainingCenterCaregiverBundleSummary[];
}

export interface TrainingCenterCaregiverBundleSummary {
  bundleId: TrainingCenterBundleId;

  title: string;
  thumbnailUrl: string | null;

  bundleParams: TrainingCenterBundleParams;

  bundleDueDates: TrainingCenterCaregiverBundleDueDateSummary[];
}

export interface TrainingCenterCaregiverBundleDueDateSummary {
  bundleDueDateId: TrainingCenterBundleDueDateId;

  year: Year;

  visibleDate: LocalDate;
  completionDate: LocalDate;
  complianceDate: LocalDate;

  visibleDateOverride: LocalDate | null;
  completionDateOverride: LocalDate | null;
  complianceDateOverride: LocalDate | null;

  dateOverridesNotes: string | null;

  visibleToCaregiver: boolean;
  isCurrentInBundle: boolean;

  completionCertificate: TrainingCenterBundleCertificate | null;

  bundleItems: TrainingCenterCaregiverBundleItemSummary[];
}

export interface TrainingCenterCaregiverBundleItemSummary {
  bundleItemId: TrainingCenterBundleItemId;

  title: string;

  itemMediaSummary: TrainingCenterBundleItemMediaSummary;

  /**
   * The test becomes unlocked when the caregiver has watched enough of the
   * training material (e.g. Video, PDF...)
   */
  testUnlocked: boolean;

  itemCompleted: boolean;
  itemExempt: boolean;

  testNumAttempts: number;
  testMaxAttemptsAllowed: number;
}

export type TrainingCenterBundleItemMediaSummary =
  | TrainingCenterBundleItemMediaSummary.Video
  | TrainingCenterBundleItemMediaSummary.PDF
  | TrainingCenterBundleItemMediaSummary.TestOnly;

export declare namespace TrainingCenterBundleItemMediaSummary {
  export interface Video {
    type: "Video";
    videoLengthSeconds: number;
    requiredWatchTimePercent: number;
    language: TrainingCenterDisplayLanguage;
    watchData: TrainingCenterCaregiverVideoWatchData;
  }

  export interface PDF {
    type: "PDF";
    requiredViewTimeMinutes: number;
    pdfWatchData: TrainingCenterCaregiverPDFWatchData;
  }

  export interface TestOnly {
    type: "TestOnly";
  }
}

export interface TrainingCenterEditCaregiverBundleDates {
  notes: string | null;
  visibleDate?: LocalDate | null;
  completionDate?: LocalDate | null;
  complianceDate?: LocalDate | null;
}

export interface TrainingCenterCaregiverBundleItemSetExemptParams {
  itemExempt: boolean;
  notes: string;
}

export type TrainingCenterBundleItemVideoMediaOverlayPosition = "TOP" | "CENTER" | "BOTTOM";
export const TrainingCenterBundleItemVideoMediaOverlayPosition = {
  TOP: "TOP" as const,
  CENTER: "CENTER" as const,
  BOTTOM: "BOTTOM" as const,
};
